import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import EditableText from "@/components/editor/EditableText";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuestBook() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [realEntries, setRealEntries] = useState([]);

  useEffect(() => {
    const fetchEntries = async () => {
      try {
        const res = await axios.get(`${API}/guestbook`);
        if (res.data.entries?.length > 0) setRealEntries(res.data.entries);
      } catch { /* show empty state */ }
    };
    fetchEntries();
  }, []);

  const displayEntries = realEntries;
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", message: "", visit_date: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/guestbook`, form);
      setSubmitted(true);
      setShowForm(false);
    } catch (err) {
      console.error("Guestbook error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="guestbook" data-testid="guestbook-section" className="wc-section bg-wc-cream" ref={ref}>
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <p className="font-accent italic text-wc-teal text-base mb-3">Guest Book</p>
          <EditableText contentKey="guestbook_heading" as="h2"
            className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4"
            defaultValue="Notes from the cove." />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
          {displayEntries.length === 0 ? (
            <div className="max-w-2xl mx-auto text-center py-10 px-6 border border-dashed border-wc-driftwood/40 rounded-sm bg-white/40" data-testid="guestbook-empty-state">
              <p className="font-accent italic text-wc-teal text-base mb-3">No notes yet.</p>
              <p className="font-body text-wc-slate text-sm leading-relaxed">
                Site One opens for the 2027 season. The first notes in the cove are still to come — yours could be one of them.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {displayEntries.map((entry, i) => (
                <div key={entry.id || i} className="guestbook-card" data-testid={`guestbook-entry-${i}`}>
                  <p className="font-body text-wc-slate text-sm leading-relaxed mt-4 mb-4">
                    {entry.message}
                  </p>
                  {entry.reply && (
                    <div className="mb-3 pl-3 border-l-2 border-wc-teal/30">
                      <p className="font-accent italic text-wc-teal text-xs">
                        Whale Cove: {entry.reply}
                      </p>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="font-display text-wc-forest text-sm font-semibold">{entry.name}</span>
                    <span className="font-accent italic text-wc-brass text-xs">{entry.visit_date}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        <div className="text-center">
          {submitted ? (
            <p className="font-body text-wc-teal text-sm" data-testid="guestbook-success">
              Thank you for sharing your experience!
            </p>
          ) : showForm ? (
            <motion.form
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleSubmit}
              className="max-w-md mx-auto space-y-4 text-left"
              data-testid="guestbook-form"
            >
              <Input
                required
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="border-wc-driftwood/40 bg-white"
                data-testid="guestbook-name-input"
              />
              <Input
                placeholder="When did you visit?"
                value={form.visit_date}
                onChange={(e) => setForm({ ...form, visit_date: e.target.value })}
                className="border-wc-driftwood/40 bg-white"
                data-testid="guestbook-date-input"
              />
              <textarea
                required
                placeholder="Leave a note..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={3}
                className="w-full rounded-md border border-wc-driftwood/40 bg-white px-3 py-2 font-body text-sm focus:outline-none focus:ring-2 focus:ring-wc-teal"
                data-testid="guestbook-message-input"
              />
              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-wc-forest text-wc-cream hover:bg-wc-teal font-body"
                  data-testid="guestbook-submit-button"
                >
                  {submitting ? "Sending..." : "Share"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="font-body border-wc-driftwood/40 text-wc-slate"
                  data-testid="guestbook-cancel-button"
                >
                  Cancel
                </Button>
              </div>
            </motion.form>
          ) : (
            <Button
              onClick={() => setShowForm(true)}
              className="bg-wc-forest text-wc-cream hover:bg-wc-teal font-body"
              data-testid="guestbook-leave-note-button"
            >
              Leave a Note
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
